import React, { useState } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { 
  FileText, Target, Shield, ArrowRight, Bot, 
  CheckCircle, Mail, Briefcase, Zap, Search,
  Clock, XCircle, Share2, Globe, HeartHandshake, Check, AlertCircle, RefreshCw, FileSearch, Edit3, Download, Lock,
  Cpu, Workflow, Filter, PenTool, LayoutTemplate, Activity, Compass
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { LandingHero } from '@/components/landing/LandingHero';
import { LandingFeatures } from '@/components/landing/LandingFeatures';
import { LandingFooter } from '@/components/landing/LandingFooter';

const AnimatedGradientText = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <span className={`pb-1 md:pb-2 text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600 dark:from-emerald-400 dark:via-teal-400 dark:to-emerald-400 animate-gradient bg-[length:200%_auto] ${className}`}>
    {children}
  </span>
);

export default function LandingPage() {
  const [isSharing, setIsSharing] = useState(false);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.2], [0, 50]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50 font-sans selection:bg-emerald-500/30 overflow-x-hidden relative">
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-60 dark:opacity-40">
        <div 
          className="absolute -top-[20%] -left-[10%] w-[70vw] h-[70vw] rounded-full bg-slate-200/50 dark:bg-emerald-900/20 blur-[120px] transform-gpu will-change-transform" 
        />
        <div 
          className="absolute top-[20%] -right-[10%] w-[60vw] h-[60vw] rounded-full bg-slate-300/30 dark:bg-teal-900/20 blur-[120px] transform-gpu will-change-transform" 
        />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] dark:opacity-10 mix-blend-overlay" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
      </div>
      {/* Navigation */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/70 dark:bg-slate-950/70 backdrop-blur-xl border-b border-slate-200 dark:border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer group hover:opacity-80 transition-opacity">
        <div className="bg-emerald-600 text-white dark:bg-emerald-500 dark:text-emerald-950 p-1.5 rounded-md dark:shadow-sm group-hover:scale-105 transition-transform">
          <FileText className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <h1 className="text-base font-bold tracking-tight text-emerald-800 dark:text-emerald-400 hidden sm:block">Resume<span className="text-emerald-600/70 dark:text-emerald-300/70 font-medium">Pro</span></h1>
      </Link>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <ThemeToggle />
            <Link to="/app" className="group relative px-3 sm:px-5 py-2 overflow-hidden rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 transition-all hover:scale-105 active:scale-95 dark:shadow-md whitespace-nowrap">
              <span className="relative z-10 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-bold">
                Get Started Free
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </header>
      <main className="relative z-10 pt-32 lg:pt-40 pb-16">
        <LandingHero heroY={heroY} heroOpacity={heroOpacity} />
        <LandingFeatures />
      </main>
      <LandingFooter />
    </motion.div>
  );
}
